/**
 * Browser input capture: keyboard, mouse look and pointer-lock lifecycle.
 *
 * Captures raw device state only. `sampleAt()` converts it into an InputFrame
 * once per simulation tick; it never interprets movement. Mouse look is
 * applied to yaw/pitch immediately (render rate) so the view is responsive.
 * The simulation reads a tick-aligned copy: every device event is applied at
 * the tick covering the moment it happened (InputTimeline, docs/INPUT_TIMING.md).
 */
import type { InputFrame } from './InputFrame';
import { InputTimeline, applyLookDelta } from './InputTimeline';

export type Action =
  | 'forward'
  | 'back'
  | 'left'
  | 'right'
  | 'jump'
  | 'crouch'
  | 'restart'
  | 'restartToStart'
  | 'toggleMode'
  | 'toggleDebug'
  | 'devBoost'
  | 'toggleDevPanel'
  | 'placeSpawn'
  | 'gotoSpawn';

export type Bindings = Record<Action, readonly string[]>;

export const DEFAULT_BINDINGS: Readonly<Bindings> = Object.freeze({
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  crouch: ['ShiftLeft', 'KeyC'],
  restart: ['KeyR'],
  restartToStart: ['KeyT'],
  toggleMode: ['KeyM'],
  toggleDebug: ['F3'],
  devBoost: ['KeyB'],
  toggleDevPanel: ['F2'],
  placeSpawn: ['KeyP'],
  gotoSpawn: ['KeyG'],
});

/** Discrete actions delivered on key-down (never on auto-repeat). */
export type CommandAction = Extract<Action, 'restart' | 'restartToStart' | 'toggleMode' | 'toggleDebug' | 'devBoost' | 'toggleDevPanel' | 'placeSpawn' | 'gotoSpawn'>;

const COMMANDS: readonly CommandAction[] = ['restart', 'restartToStart', 'toggleMode', 'toggleDebug', 'devBoost', 'toggleDevPanel', 'placeSpawn', 'gotoSpawn'];
const FORM_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON']);
const PITCH_LIMIT =(89 * Math.PI) / 180;
/** Radians per mouse count at sensitivity 1. */
const BASE_LOOK_SCALE = 0.0022;

export interface InputCallbacks {
  onCommand(action: CommandAction): void;
  onLockChange(locked: boolean): void;
  onLockError(message: string): void;
}

export class InputManager {
  private readonly codeToAction = new Map<string, Action>();
  private readonly timeline = new InputTimeline(PITCH_LIMIT);
  /** Live view angles (render rate). */
  private readonly live = { yaw: 0, pitch: 0 };
  sensitivity = 1;
  /** When true, keyboard input counts even without pointer lock (automated tests). */
  allowUnlockedInput = false;
  private locked = false;
  private readonly abort = new AbortController();

  constructor(
    private readonly element: HTMLElement,
    private readonly callbacks: InputCallbacks,
    bindings: Readonly<Bindings> = DEFAULT_BINDINGS,
  ) {
    this.setBindings(bindings);
    const opts = { signal: this.abort.signal };
    const doc = element.ownerDocument;
    doc.addEventListener('keydown', (e) => this.onKeyDown(e), opts);
    doc.addEventListener('keyup', (e) => this.onKeyUp(e), opts);
    doc.addEventListener('mousemove', (e) => this.onMouseMove(e), opts);
    doc.addEventListener('pointerlockchange', () => this.onLockChange(), opts);
    doc.addEventListener('pointerlockerror', () => this.callbacks.onLockError('The browser refused pointer lock. Click the game view to try again.'), opts);
    doc.addEventListener('visibilitychange', () => doc.visibilityState === 'hidden' && this.clear(), opts);
    doc.defaultView?.addEventListener('blur', () => this.clear(), opts);
  }

  setBindings(bindings: Readonly<Bindings>): void {
    this.codeToAction.clear();
    for (const [action, codes] of Object.entries(bindings) as [Action, readonly string[]][]) {
      for (const code of codes) {
        if (this.codeToAction.has(code)) throw new Error(`Key ${code} bound to both ${this.codeToAction.get(code)} and ${action}`);
        this.codeToAction.set(code, action);
      }
    }
    this.clear();
  }

  /** Live view yaw (render rate). Setting it also sets the simulation's look and drops pending look deltas. */
  get yaw(): number {
    return this.live.yaw;
  }

  set yaw(v: number) {
    this.live.yaw = v;
    this.timeline.setLook(v, this.live.pitch);
  }

  get pitch(): number {
    return this.live.pitch;
  }

  set pitch(v: number) {
    this.live.pitch = v;
    this.timeline.setLook(this.live.yaw, v);
  }

  get isLocked(): boolean {
    return this.locked;
  }

  /** Must be called from a user gesture (click). Resolves once locked or rejects with a reason. */
  async requestLock(): Promise<void> {
    const el = this.element as HTMLElement & { requestPointerLock(options?: { unadjustedMovement?: boolean }): Promise<void> | void };
    try {
      // Raw (unaccelerated) input where supported; not all browsers support the option.
      await el.requestPointerLock({ unadjustedMovement: true });
    } catch (first: unknown) {
      try {
        await el.requestPointerLock();
      } catch (second: unknown) {
        const reason = second instanceof Error ? second.message : String(second ?? first);
        this.callbacks.onLockError(`Pointer lock failed: ${reason}`);
        throw second instanceof Error ? second : new Error(reason);
      }
    }
  }

  releaseLock(): void {
    if (this.element.ownerDocument.pointerLockElement === this.element) this.element.ownerDocument.exitPointerLock();
  }

  /** Drops all held keys and pending edges, e.g. on focus loss, so nothing stays stuck. */
  clear(): void {
    this.timeline.reset(this.live.yaw, this.live.pitch);
  }

  /** Fills `out` with every input up to now and consumes jump edges. */
  sample(out: InputFrame): InputFrame {
    return this.sampleAt(out, Infinity);
  }

  /**
   * Fills `out` with the input state as of `tickEndMs` (the wall-clock end of the
   * tick being simulated, on the DOM event clock) and consumes jump edges.
   * Events after that moment stay queued for a later tick.
   */
  sampleAt(out: InputFrame, tickEndMs: number): InputFrame {
    this.timeline.drainTo(tickEndMs);
    return this.timeline.fill(out, this.locked || this.allowUnlockedInput);
  }

  /** Keeps held keys current while a replay drives the ticks, without letting a press fire after it. */
  skipPending(): void {
    this.timeline.discardPending();
  }

  dispose(): void {
    this.abort.abort();
    this.releaseLock();
  }

  private onKeyDown(e: KeyboardEvent): void {
    const action = this.codeToAction.get(e.code);
    if (!action) return;
    // Keys aimed at a focused form control (developer panel, settings) belong
    // to that control; only function-key commands (F2, F3) still pass.
    const target = e.target as { tagName?: string } | null;
    if (!this.locked && target && FORM_TAGS.has(target.tagName ?? '') && !/^F\d+$/.test(e.code)) return;
    // Keep browser shortcuts (F3 find, Space scroll) from stealing game keys.
    e.preventDefault();
    // Repeats still mark the key held: after clear() (focus/resume) a key that
    // is physically down only produces repeat events until it is released.
    const jumpEdge = !e.repeat && action === 'jump' && (this.locked || this.allowUnlockedInput);
    this.timeline.push({ t: e.timeStamp, kind: 'down', code: e.code, action, jumpEdge });
    if (e.repeat) return;
    if ((COMMANDS as readonly Action[]).includes(action)) {
      // A command (restart, mode change) acts now, before the pending ticks run. Input that
      // happened before it must already be applied, or e.g. a key released just before a
      // restart would still walk the respawned player for the rest of the frame.
      this.timeline.drainTo(e.timeStamp);
      this.callbacks.onCommand(action as CommandAction);
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    if (!this.codeToAction.has(e.code)) return;
    e.preventDefault();
    this.timeline.push({ t: e.timeStamp, kind: 'up', code: e.code });
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.locked) return;
    const k = BASE_LOOK_SCALE * this.sensitivity;
    const dyaw = -e.movementX * k;
    const dpitch = -e.movementY * k;
    applyLookDelta(this.live, dyaw, dpitch, PITCH_LIMIT);
    this.timeline.push({ t: e.timeStamp, kind: 'look', dyaw, dpitch });
  }

  private onLockChange(): void {
    const now = this.element.ownerDocument.pointerLockElement === this.element;
    if (now === this.locked) return;
    this.locked = now;
    this.clear();
    this.callbacks.onLockChange(now);
  }
}
